import { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Dialog,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../components/SnackBarCustom";
import { getIcons } from "../../../../api";
import type { IconItem } from "../../../../types/icon";

type MapIconColumnId = "icon" | "sales" | "sale_status" | "damage";

type MapIconColumn = {
  id: MapIconColumnId;
  label: string;
  minWidth: string;
  icon?: boolean;
};

const columns: MapIconColumn[] = [
  { id: "icon", label: "アイコン", minWidth: "40px", icon: true },
  { id: "sales", label: "売上有無", minWidth: "40px" },
  { id: "sale_status", label: "営業状況", minWidth: "40px" },
  { id: "damage", label: "被害有無", minWidth: "40px" },
];

const getIconFieldValue = (icon: IconItem, columnId: MapIconColumnId): number => {
  if (columnId === "sales") return icon.sales;
  if (columnId === "sale_status") return icon.sale_status;
  if (columnId === "damage") return icon.damage;
  return 0;
};

const formatIconField = (
  columnId: MapIconColumnId,
  value: number
): string => {
  if (columnId === "sales") {
    if (value === 0) return "なし※1";
    if (value === 1) return "あり※1";
    if (value === 9) return "不明※1";
    return "-";
  }
  if (columnId === "sale_status") {
    if (value === 0) return "休業※2";
    if (value === 1) return "営業※2";
    if (value === 2) return "未設定※2";
    if (value === 9) return "不明※2";
    return "-";
  }
  if (columnId === "damage") {
    if (value === 0) return "なし※2";
    if (value === 1) return "あり※2";
    if (value === 2) return "未設定※2";
    if (value === 9) return "不明※2";
    return "-";
  }
  return "-";
};

export default function MapIconTable() {
  const sessionStorageTenantId = sessionStorage.getItem("tenant_id");
  const [selectedRow, setSelectedRow] = useState<IconItem | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [icons, setIcons] = useState<IconItem[]>([]);
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchIcons = async () => {
    try {
      const response = await getIcons({
        m_tenant_id: sessionStorageTenantId,
        page: null,
        limit: 30,
      });
      if (response.status !== "ok") {
        return;
      }
      setIcons(response.m_icons);
      setSnackBarstatus(response);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    fetchIcons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <TableContainer>
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{
            writingMode: "vertical-lr",
            "@media screen and (max-width:900px)": {
              writingMode: "horizontal-tb",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    padding: "8px",
                    backgroundColor:
                      column.id === "icon" ? "outline.main" : "#FFF",
                    color: "outline.dark",
                    fontWeight: "bold",
                    borderBottom: 2,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {icons.map((icon, index) => (
              <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={icon.id}
                onClick={() => {
                  setSelectedRow(icon);
                  setIsPopoverOpen(true);
                }}
                sx={{
                  pointerEvents: "none",
                  "@media screen and (max-width:900px)": {
                    cursor: "pointer",
                    pointerEvents: "auto",
                  },
                }}
                name={`iconData_${index}`}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      padding: "8px",
                      textAlign: "center",
                      writingMode: "horizontal-tb",
                      backgroundColor:
                        column.id === "icon" ? "outline.main" : "#FFF",
                    }}
                  >
                    {column.icon ? (
                      <img
                        src={`https://ljs-dic-public-resources-dev.s3.ap-northeast-1.amazonaws.com/${icon.file_key}`}
                        alt="Example"
                        style={{ width: "56px", height: "auto" }}
                      />
                    ) : (
                      formatIconField(column.id, getIconFieldValue(icon, column.id))
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={isPopoverOpen} onClose={() => setIsPopoverOpen(false)}>
        <Box
          sx={{
            display: "flex",
            height: "56px",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px",
            borderBottom: "2px solid #CCC",
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            zIndex: 1,
          }}
        >
          <Box sx={{ paddingLeft: "8px", fontWeight: "bold", fontSize: "20px" }}>
            マップアイコン詳細
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setIsPopoverOpen(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
          <TableBody>
            {selectedRow &&
              columns.map((column) => (
                <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                  <TableCell
                    sx={{
                      color: "outline.dark",
                      fontWeight: "bold",
                      boxSizing: "border-box",
                      width: "calc((100vw - 64px) / 2)",
                      overflowWrap: "break-word",
                      backgroundColor:
                        column.id === "icon" ? "outline.main" : "#FFF",
                    }}
                  >
                    {column.label}
                  </TableCell>
                  <TableCell
                    sx={{
                      writingMode: "horizontal-tb",
                      boxSizing: "border-box",
                      width: "calc((100vw - 64px) / 2)",
                      overflowWrap: "break-word",
                      backgroundColor:
                        column.id === "icon" ? "outline.main" : "#FFF",
                    }}
                  >
                    {column.icon ? (
                      <img
                        src={`https://ljs-dic-public-resources-dev.s3.ap-northeast-1.amazonaws.com/${selectedRow.file_key}`}
                        alt="Example"
                        style={{ width: "56px", height: "auto" }}
                      />
                    ) : (
                      formatIconField(
                        column.id,
                        getIconFieldValue(selectedRow, column.id)
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Dialog>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: "8px",
        }}
      >
        <Box sx={{ color: "error.main", fontWeight: "bold" }}>
          ※1: システム自動判定 &nbsp;&nbsp;&nbsp;&nbsp; ※2: 災害報告入力判定
        </Box>
      </Box>
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
    </Paper>
  );
}
